import React, { useState } from 'react';
import { PlayFab } from '../app/services/PlayFab';
import { BsEnvelope, BsCheckCircle, BsArrowRight } from 'react-icons/bs';
import './EmailCaptureForm.css';

interface EmailCaptureFormProps {
  campaign: string;
  buttonText?: string;
  placeholder?: string;
}

export const EmailCaptureForm: React.FC<EmailCaptureFormProps> = ({ 
  campaign, 
  buttonText = "Join Waitlist", 
  placeholder = "Enter your email" 
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const validateEmail = (email: string): boolean => {
    // Basic email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous error
    setErrorMessage('');
    
    // Trim whitespace
    const trimmedEmail = email.trim();
    
    // Check if email is empty
    if (!trimmedEmail) {
      setStatus('error');
      setErrorMessage('Please enter your email address.');
      return;
    }
    
    // Validate email format
    if (!validateEmail(trimmedEmail)) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setStatus('loading');

    try {
      // Use email as CustomID (sanitized)
      const customId = trimmedEmail.toLowerCase().replace(/[^a-z0-9@._-]/g, '_');
      
      const loginResult = await PlayFab.loginWithCustomID(customId);
      
      if (loginResult.success) {
        // Update user data with the actual email and campaign info
        const updateResult = await PlayFab.updateUserData({
          ContactEmail: trimmedEmail,
          Campaign: campaign,
          SignupDate: new Date().toISOString()
        }, "Private");

        if (updateResult) {
          setStatus('success');
          setEmail('');
          
          // Track custom goal with DataFast
          if (window.datafast) {
            window.datafast('email_submitted', {
              campaign: campaign,
              method: 'custom_id'
            });
          }
        } else {
          throw new Error('Failed to save email.');
        }
      } else {
        throw new Error('Login failed.');
      }
    } catch (error) {
      console.error('Email capture error:', error);
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className="email-capture-success">
        <BsCheckCircle className="success-icon" />
        <p>Thanks! We'll keep you posted.</p>
      </div>
    );
  }

  return (
    <form className="email-capture-form" onSubmit={handleSubmit}>
      <div className="input-group">
        <div className="input-icon">
          <BsEnvelope />
        </div>
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            // Clear error when user starts typing
            if (status === 'error') {
              setStatus('idle');
              setErrorMessage('');
            }
          }}
          placeholder={placeholder}
          disabled={status === 'loading'}
          required
        />
      </div>
      <button type="submit" disabled={status === 'loading'}>
        {status === 'loading' ? 'Joining...' : buttonText}
        {status !== 'loading' && <BsArrowRight className="button-icon" />}
      </button>
      {errorMessage && <p className="error-message">{errorMessage}</p>}
    </form>
  );
};
